/**
 * Angebots-Detail.
 *
 * Zweispaltig: links Status, Beteiligte, Mail-Verlauf, Gesprächsnotizen und
 * Statusverlauf – rechts der Datensatz und die weiteren Angebote des Kunden.
 *
 * Zwei Warnbanner sind bewusst dauerhaft, nicht flüchtig:
 *  - fehlgeschlagener Sync zu Monday/SharePoint
 *  - in Monday pausiert, aber ohne Wiedervorlage-Datum
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import {
  IconGesendet,
  IconMail,
  IconMonday,
  IconSharepoint,
  IconStift,
  IconTelefon,
  IconWarnung,
} from '../komponenten/Icons';
import { AuftragModal } from '../modals/AuftragModal';
import { VersendetModal } from '../modals/VersendetModal';
import { GewerkModal } from '../modals/GewerkModal';
import { AnrufModal } from '../modals/AnrufModal';
import { MailVorschauModal } from '../modals/MailVorschauModal';
import { KundenModal } from '../modals/KundenModal';
import { dat, istMobil, whatsappNummer } from '../hilfen/format';
import { Betragsfeld } from '../komponenten/Betragsfeld';
import type { Angebot, AngebotAntwort } from '../typen';
import './detail.css';


/**
 * Der Explorer-Link zu einem Ordner.
 *
 * Der Ordner liegt je nach Status in „Angebote" oder „Aufträge" – das
 * Protokoll-Programm bekommt beides mit und sucht dort. Ein file://-Link ginge
 * nicht: Browser blockieren ihn.
 */
function explorerLink(status: string, ordnerName: string): string {
  const art = status === 'Auftrag erteilt' || status === 'Abgenommen' ? 'auftrag' : 'angebot';
  return `kora://oeffne?art=${art}&ordner=${encodeURIComponent(ordnerName)}`;
}

/**
 * Auswahl, in welches Gewerk gesprungen wird.
 *
 * Bei einem einzelnen Gewerk ist ein Link richtig; laufen mehrere unter einer
 * Nummer, wäre ein Link eine Entscheidung, die Kora nicht treffen kann. Das
 * eigene Gewerk steht oben und ist markiert – es ist der häufigste Fall.
 */
function Quellenwahl({
  titel,
  symbol,
  eintraege,
}: {
  titel: string;
  symbol: ReactNode;
  eintraege: {
    schluessel: string;
    label: string;
    eigenes: boolean;
    ziel: string;
    extern: boolean;
    hinweis?: string;
  }[];
}) {
  const [offen, setOffen] = useState(false);
  if (eintraege.length === 0) return null;

  const sortiert = [...eintraege].sort((x, y) => Number(y.eigenes) - Number(x.eigenes));

  return (
    <span className="detail-quellenwahl">
      <button
        type="button"
        className="detail-quelle"
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
      >
        {symbol}
        {titel}
        <span className="detail-quelle-pfeil">{offen ? '▴' : '▾'}</span>
      </button>

      {offen && (
        <span className="detail-quellenmenue">
          {sortiert.map((e) => (
            <a
              key={e.schluessel}
              href={e.ziel}
              target={e.extern ? '_blank' : undefined}
              rel={e.extern ? 'noreferrer' : undefined}
              title={e.hinweis}
              onClick={() => setOffen(false)}
            >
              <span className="detail-quellenmenue-label">
                {e.label}
                {e.eigenes && <span className="detail-quellenmenue-marke">geöffnet</span>}
              </span>
              {e.hinweis && <span className="detail-quellenmenue-pfad">{e.hinweis}</span>}
            </a>
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * Wohin der Zurück-Link führt, je nachdem woher man kam.
 *
 * Der Schlüssel reist im Routing-Zustand mit. Wer hier eine Herkunft ergänzt,
 * muss sie auch an der aufrufenden Stelle mitgeben – sonst greift der Fallback.
 */
const HERKUENFTE: Record<string, { key: string; pfad: string; name: string }> = {
  cockpit: { key: 'cockpit', pfad: '/cockpit', name: 'Vertriebscockpit' },
  kunden: { key: 'kunden', pfad: '/kunden', name: 'Kundenakte' },
  meintag: { key: 'meintag', pfad: '/', name: 'Mein Tag' },
  auftraege: { key: 'auftraege', pfad: '/auftraege', name: 'Aufträge' },
};

export function AngebotDetail() {
  const { id } = useParams();
  const angebotId = Number(id);
  const navigate = useNavigate();
  const ort = useLocation();
  const queryClient = useQueryClient();
  const { toastZeigen, nutzer } = useKora();
  const meta = useMeta();

  /** Aufgeklappte Löschabfrage. Zweistufig, weil der Schritt endgültig ist. */
  const [loeschAbfrage, setLoeschAbfrage] = useState(false);
  const [auftragOffen, setAuftragOffen] = useState(false);
  const [versendetOffen, setVersendetOffen] = useState(false);
  const [gewerkOffen, setGewerkOffen] = useState(false);
  const [anrufOffen, setAnrufOffen] = useState(false);
  const [mailOffen, setMailOffen] = useState(false);
  const [kalkWechsel, setKalkWechsel] = useState(false);
  const [kundeOffen, setKundeOffen] = useState(false);
  const [mehrOffen, setMehrOffen] = useState(false);
  /** Die Chronik startet zu – sichtbar bleibt nur die letzte Aktivität. */
  const [chronikOffen, setChronikOffen] = useState(false);
  const [chronikFilter, setChronikFilter] = useState<
    'alle' | 'status' | 'aenderung' | 'mail' | 'rueckmeldung'
  >('alle');

  const abfrage = useQuery({
    queryKey: ['angebot', angebotId],
    queryFn: () => api.hole<Angebot>(`/angebote/${angebotId}`),
    enabled: Number.isFinite(angebotId),
  });

  const alleAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  function aktualisieren() {
    void queryClient.invalidateQueries({ queryKey: ['angebot', angebotId] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  }

  /**
   * Zuständigkeit wechseln. Schreibt auch die Personenspalte in Monday –
   * dort schaut man ja gerade deshalb hin.
   */
  const kalkulantMutation = useMutation({
    mutationFn: (kalkulant: string) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/kalkulant`, { kalkulant }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setKalkWechsel(false);
      toastZeigen('Zuständigkeit gewechselt – Monday ist nachgezogen.');
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Wechsel fehlgeschlagen.'),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      api.sende<AngebotAntwort>(`/angebote/${angebotId}/status`, { status }),
    onSuccess: (a) => {
      aktualisieren();
      if (a.sync.fehler) toastZeigen('Status gespeichert – der Abgleich meldet ein Problem.');
    },
  });

  /**
   * Löscht den Vorgang in Kora.
   *
   * Nur für Fehlgriffe und Testanlagen, deshalb der Administration
   * vorbehalten. Kora räumt ausschließlich die eigenen Daten weg; was in
   * Monday und SharePoint liegt, bleibt stehen und wird in der Rueckmeldung
   * benannt. Anschließend zurück zur Liste – die Seite hätte keinen
   * Datensatz mehr zum Anzeigen.
   */
  const loeschMutation = useMutation({
    mutationFn: () =>
      api.loesche<{ nr: number; nummerFrei: boolean; offen: string[] }>(
        `/angebote/${angebotId}`,
      ),
    onSuccess: (a) => {
      const teile = [`Angebot ${a.nr} gelöscht`];
      if (a.nummerFrei) teile.push('die Nummer ist wieder frei');
      if (a.offen.length) teile.push(`nicht entfernt: ${a.offen.join(', ')}`);
      toastZeigen(teile.join(' - '));
      void queryClient.invalidateQueries();
      navigate('/cockpit');
    },
    onError: (f: Error) => toastZeigen(f.message),
  });

  /**
   * Verknüpfung zu Monday und SharePoint neu suchen.
   *
   * Für den Fall, dass dort etwas gelöscht und unter derselben Nummer neu
   * angelegt wurde. Das Ergebnis wird ausführlich gemeldet und nicht nur als
   * „erledigt" quittiert: Findet die Suche nichts oder zu viel, muss der
   * Benutzer wissen, woran er ist.
   */
  const verknuepfungMutation = useMutation({
    mutationFn: () =>
      api.sende<{
        gesucht: string;
        monday: { stand: string; text: string };
        sharepoint: { stand: string; text: string };
        geaendert: boolean;
      }>(`/angebote/${angebotId}/verknuepfung`),
    onSuccess: (r) => {
      aktualisieren();
      toastZeigen(`Monday: ${r.monday.text} SharePoint: ${r.sharepoint.text}`);
    },
    onError: (f: Error) => toastZeigen(f.message),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/angebote/${angebotId}/sync`),
    onSuccess: (a) => {
      aktualisieren();
      if (a.toast) toastZeigen(a.toast);
    },
  });

  const wertMutation = useMutation({
    mutationFn: (auftrag: number | null) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/auftragswert`, { auftrag }),
    onSuccess: aktualisieren,
  });



  const a = abfrage.data;

  const geschwister = useMemo(
    () => (alleAbfrage.data ?? []).filter((x) => a && x.kunde.id === a.kunde.id && x.id !== a.id),
    [alleAbfrage.data, a],
  );

  if (abfrage.isLoading) {
    return <div className="eh-inhalt">Angebot wird geladen …</div>;
  }
  if (!a) {
    return (
      <div className="eh-inhalt">
        <div className="eh-karte">Dieses Angebot gibt es nicht (mehr).</div>
      </div>
    );
  }

  /**
   * Der Fortschritt als Zeitleiste – nur Anzeige, nichts zum Klicken.
   *
   * Gesetzt wird oben über „Status ändern". Hier steht, wo der Vorgang steht
   * und wie er dahin kam: je Station, wer sie gesetzt hat und wann. Der frühere
   * Erklärtext ist entfallen – er sagte, warum etwas noch nicht geht, und das
   * beantwortet sich, sobald die Stationen leer oder gefüllt sind.
   *
   * „Rückmeldung erfassen" steht bewusst nicht hier: Ein Kundenanruf ist kein
   * Status, den jemand gesetzt hat, sondern ein Ereignis.
   */
  const stationen = ['START', 'in Arbeit', 'Freigabe Technik', 'Gesendet', 'Auftrag erteilt', 'Abgenommen'];

  const erreicht = new Map<string, { user: string; datumText: string }>();
  for (const v of a.verlauf) {
    // Bei mehrfach durchlaufenen Stationen zählt der letzte Durchgang.
    erreicht.set(v.status, { user: v.user, datumText: v.datumText });
  }
  /*
   * START ist bei jedem Vorgang durchlaufen – ohne Anlage gäbe es ihn nicht.
   * Für in Kora angelegte Angebote steht der Eintrag im Verlauf; für die aus
   * Monday übernommenen fehlt er, dort bleibt wenigstens das Anlagedatum.
   */
  if (!erreicht.has('START')) {
    erreicht.set('START', { user: '', datumText: dat(a.angelegtAm) });
  }

  /**
   * Wohin führt ein gewählter Status?
   *
   * Manche Wechsel brauchen Angaben – ein Versanddatum, eine Auftragshöhe,
   * eine Begründung. Statt sie stumm zu setzen, öffnet die Auswahl den
   * passenden Dialog. So gibt es einen Ort für den Statuswechsel, ohne dass
   * Pflichtangaben verlorengehen.
   */
  function statusWaehlen(ziel: string) {
    if (!ziel) return;
    if (ziel === 'Gesendet') { setVersendetOffen(true); return; }
    if (ziel === 'Auftrag erteilt') { setAuftragOffen(true); return; }
    if (ziel === 'Wiedervorlage' || ziel === 'Abgesagt') { setAnrufOffen(true); return; }
    statusMutation.mutate(ziel);
  }

  /**
   * Chronik: Statuswechsel, Mails und Rückmeldungen in einem Strom.
   *
   * Vorher standen sie in drei getrennten Karten untereinander und machten die
   * Seite lang, obwohl fast immer nur der letzte Eintrag interessiert. Jetzt
   * bleibt sichtbar, was zuletzt geschah; alles Übrige liegt eine Klick tief.
   */
  type ChronikArt = 'status' | 'mail' | 'rueckmeldung' | 'aenderung';

  const chronik: {
    id: string;
    art: ChronikArt;
    /** Sonderfall: die Anlage. Sie ist ein Statuswechsel, liest sich aber nicht so. */
    anlage: boolean;
    sortier: string;
    titel: string;
    unter: string;
    notiz: string;
    zeitText: string;
  }[] = [
    ...a.verlauf.map((v, i) => {
      const anlage = v.status === 'START' && i === 0;
      return {
        id: `s${i}`,
        art: 'status' as ChronikArt,
        anlage,
        sortier: `${v.datum} ${v.zeit || '00:00'}`,
        titel: anlage ? 'Angebot angelegt' : `Status → ${v.status}`,
        unter: anlage ? `durch ${v.user}` : `in KORA durch ${v.user}`,
        notiz: anlage ? '' : v.notiz,
        zeitText: `${v.wochentag}, ${v.datumText}${v.zeit ? ` · ${v.zeit}` : ''}`,
      };
    }),
    ...a.erinnerungen.map((e) => ({
      id: `m${e.id}`,
      art: 'mail' as ChronikArt,
      anlage: false,
      sortier: `${e.datum} ${e.zeit}`,
      titel:
        e.art === 'auto'
          ? `${e.stufe}. Erinnerung automatisch versendet`
          : `${e.stufe}. Erinnerung ${e.artText}`,
      unter:
        `im Namen von ${e.user}` +
        (e.trockenlauf
          ? ' · Testbetrieb, nur als Entwurf abgelegt'
          : a.nachfass.antwortAm
            ? ` · Kundenantwort am ${dat(a.nachfass.antwortAm)} erkannt`
            : ' · keine Antwort erkannt'),
      notiz: '',
      zeitText: `${e.wochentag}, ${e.datumText} · ${e.zeit}`,
    })),
    ...a.aenderungen.map((x) => ({
      id: `a${x.id}`,
      art: 'aenderung' as ChronikArt,
      anlage: false,
      sortier: `${x.datum} ${x.zeit}`,
      titel: `${x.feld} geändert`,
      unter: `in KORA durch ${x.user}`,
      // Der alte Wert gehört dazu: Ohne ihn sagt der Eintrag nicht, was geschah.
      notiz: `${x.vorher || 'nicht gesetzt'}  →  ${x.nachher || 'entfernt'}`,
      zeitText: `${x.wochentag}, ${x.datumText} · ${x.zeit}`,
    })),
    ...a.anrufe.map((c) => ({
      id: `r${c.id}`,
      art: 'rueckmeldung' as ChronikArt,
      anlage: false,
      sortier: `${c.datum} ${c.zeit}`,
      titel:
        'Rückmeldung · ' +
        (c.wiedervorlage
          ? `Wiedervorlage für ${dat(c.wiedervorlage)} vereinbart`
          : c.ergebnisLabel),
      unter:
        `${c.kanalLabel} · erfasst von ${c.user}` +
        (c.statusGesetzt
          ? ` · Status → ${c.statusGesetzt}`
          : c.wiedervorlage
            ? ' · Nachfassen ruhte seither'
            : ''),
      notiz: c.notiz,
      zeitText: `${c.wochentag}, ${c.datumText} · ${c.zeit}`,
    })),
  ].sort((x, y) => (x.sortier < y.sortier ? 1 : -1));

  /** Zähler je Filter – sie stehen im Chip, damit niemand blind hineinklickt. */
  const chronikZahl = {
    alle: chronik.length,
    status: chronik.filter((c) => c.art === 'status').length,
    aenderung: chronik.filter((c) => c.art === 'aenderung').length,
    mail: chronik.filter((c) => c.art === 'mail').length,
    rueckmeldung: chronik.filter((c) => c.art === 'rueckmeldung').length,
  };
  const letzteAktivitaet = chronik[0] ?? null;
  const chronikGefiltert = chronikFilter === 'alle' ? chronik : chronik.filter((c) => c.art === chronikFilter);

  const versendet = a.versandHistorie.length > 0;

  /**
   * Stand des Nachfassens als eine Zeile.
   *
   * „pausiert" stand hier vorher allein und sagte nichts: Der häufigste Grund
   * ist, dass das Angebot schlicht noch nicht beim Kunden liegt – dann gibt es
   * nichts nachzufassen. Jede Zeile nennt jetzt den Grund.
   *
   * Reihenfolge ist Absicht: Zuerst die Fälle, die das Nachfassen ganz
   * ausschließen, dann die, die es aufschieben, zuletzt der Normalfall.
   */
  const nachfassText = a.nachfass.antwortAm
    ? `Kunde hat am ${dat(a.nachfass.antwortAm)} geantwortet`
    : !versendet
      ? 'startet mit dem Versand'
      : !meta.aktiveStatus.includes(a.status)
        ? `entfällt – Status ${a.status}`
        : a.wvZustand
          ? a.wvText
          : a.nachfass.mailVerfallen
            ? `Anruf dran seit ${dat(a.nachfass.anrufAb)}`
            : a.nachfass.erledigt < 1
              ? `1. Erinnerung am ${dat(a.nachfass.mailAm)}`
              : a.nachfass.anrufFaellig
                ? `Anruf ausstehend seit ${dat(a.nachfass.anrufAb)}`
                : `Anruf fällig am ${dat(a.nachfass.anrufAb)}`;

  const nachfassTon = a.nachfass.antwortAm
    ? 'gruen'
    : a.wvZustand === 'ueberfaellig' || a.nachfass.anrufFaellig
      ? 'rot'
      : a.wvZustand === 'faellig'
        ? 'gelb'
        : 'neutral';


  /*
   * Woher kam der Aufruf?
   *
   * Wer aus der Kundenakte kommt, will dorthin zurück, nicht ins Cockpit. Die
   * Herkunft reist als Routing-Zustand mit; fehlt sie – etwa weil jemand den
   * Link direkt aufgerufen oder ein Lesezeichen benutzt hat –, ist das
   * Vertriebscockpit die vernünftigste Annahme.
   */
  const herkunft = HERKUENFTE[(ort.state as { herkunft?: string } | null)?.herkunft ?? ''] ?? HERKUENFTE.cockpit!;

  return (
    <div className="detail">
      {/*
        Das Ziel richtet sich nach der Herkunft – wer aus der Kundenakte kam,
        will dorthin zurück. Die Beschriftung bleibt trotzdem immer „Zurück":
        Wohin es geht, ist im Moment des Klickens ohnehin klar, und wechselnde
        Beschriftungen machen eine Leiste unruhig. Das Ziel steht im Tooltip.
      */}
      <button
        type="button"
        className="detail-zurueck"
        title={`Zurück zu ${herkunft.name}`}
        onClick={() => navigate(herkunft.pfad)}
      >
        ← Zurück
      </button>

      <div className="detail-kopf">
        <div className="detail-kategorie">
          <span className="detail-prefix">{a.prefix}</span>
          <span className="detail-kategorie-text">
            {a.kategorie} · {a.kunde.ort} · angelegt {dat(a.angelegtAm)}
          </span>
        </div>

        <h1 className="detail-name">{a.name}</h1>

        {/*
          Die Quellen stehen in einer eigenen Zeile unter dem Titel, nicht
          daneben: Neben einem Titel, dessen Länge vom Kundennamen abhängt,
          rutschten sie je nach Vorgang an eine andere Stelle – mal daneben,
          mal umgebrochen. Sie führen woandershin und verändern nichts,
          deshalb Links und keine Knöpfe.
        */}
        <div className="detail-quellen">
          {a.sammelvorgang ? (
            /*
              Läuft mehr als ein Gewerk unter der Nummer, muss man wählen
              können, wohin man springt. Fenster, Beschattung und
              Insektenschutz haben jeweils ein eigenes Monday-Element; der
              Ordner ist bei älteren Vorgängen noch geteilt, bei neuen nicht
              mehr. Beides bildet die Auswahl ab.
            */
            <>
              <Quellenwahl
                titel="Monday"
                symbol={<IconMonday groesse={14} />}
                eintraege={a.geschwisterGewerke
                  .filter((g) => g.mondayUrl)
                  .map((g) => ({
                    schluessel: g.prefix,
                    label: `${g.prefix} · ${g.kategorie}`,
                    eigenes: g.eigenes,
                    ziel: g.mondayUrl!,
                    extern: true,
                  }))}
              />
              <Quellenwahl
                titel="SharePoint"
                symbol={<IconSharepoint groesse={14} />}
                eintraege={a.geschwisterGewerke.map((g) => ({
                  schluessel: g.prefix,
                  label:
                    `${g.prefix} · ${g.kategorie}` +
                    (g.gleicherOrdner && !g.eigenes ? ' · gemeinsamer Ordner' : ''),
                  eigenes: g.eigenes,
                  ziel: explorerLink(a.status, g.ordnerName),
                  extern: false,
                  hinweis: g.ordnerName,
                }))}
              />
            </>
          ) : (
            <>
              {a.mondayUrl && (
                <a className="detail-quelle" href={a.mondayUrl} target="_blank" rel="noreferrer">
                  <IconMonday groesse={14} />
                  Monday
                  <span className="detail-quelle-pfeil">↗</span>
                </a>
              )}
              <a
                className="detail-quelle"
                href={explorerLink(a.status, a.ordnerName)}
                title={`Öffnet ${a.ordnerName} im Windows-Explorer`}
              >
                <IconSharepoint groesse={14} />
                SharePoint
                <span className="detail-quelle-pfeil">↗</span>
              </a>
            </>
          )}
        </div>
      </div>

      {a.syncFehler && (
        <div className="detail-warnung">
          <IconWarnung groesse={16} />
          <div className="detail-warnung-text">
            <div className="detail-warnung-titel">
              Status lokal gesetzt, Abgleich zu Monday/SharePoint fehlgeschlagen
            </div>
            <div className="detail-warnung-unter">{a.syncFehler}</div>
          </div>
          <button
            type="button"
            className="detail-warnung-knopf"
            disabled={syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            {syncMutation.isPending ? 'Läuft …' : 'Erneut versuchen'}
          </button>
        </div>
      )}

      {a.wvKonflikt && (
        <div className="detail-warnung">
          <IconWarnung groesse={16} />
          <div className="detail-warnung-text">
            <div className="detail-warnung-titel">In Monday pausiert, aber ohne Termin</div>
            <div className="detail-warnung-unter">
              Ohne Wiedervorlage-Datum taucht der Vorgang nie wieder auf. Bitte über
              „Rückmeldung erfassen" einen Termin hinterlegen.
            </div>
          </div>
        </div>
      )}

      <div className="detail-raster">
        <div className="detail-spalte">
          <div className="detail-karte detail-statuskarte">
            {/* ---- Statuszeile: der Status genau einmal ---- */}
            <div className="detail-statuszeile">
              <span className="detail-statuspunkt" style={{ background: a.statusPunkt }} />
              <span className="detail-statusname">{a.status}</span>
              <span className="detail-chip">{a.alterText} im Status</span>

              {a.wvZustand && (
                <span className={`detail-chip wv-${a.wvZustand}`} title={a.wvHerkunft}>
                  <span className="detail-chip-punkt" />
                  {a.wvText}
                </span>
              )}

              <div className="detail-statusaktionen">
                {/*
                  Der Statuswechsel ist die einzige gefüllte Aktion, weil der
                  Status jetzt in Kora gepflegt wird. Das Auswahlfeld liegt
                  unsichtbar über dem Knopf, damit das native Menü aufgeht.
                */}
                <label className="detail-statuswahl">
                  <span>Status ändern</span>
                  <span className="detail-statuswahl-pfeil">▾</span>
                  <select
                    value=""
                    disabled={statusMutation.isPending}
                    onChange={(e) => {
                      statusWaehlen(e.target.value);
                      e.target.value = '';
                    }}
                  >
                    <option value="">wählen …</option>
                    {meta.status
                      .filter((x) => x !== a.status)
                      .map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                  </select>
                </label>

                <button
                  type="button"
                  className="detail-zweitknopf"
                  onClick={() => setAnrufOffen(true)}
                >
                  <IconTelefon groesse={15} />
                  Rückmeldung erfassen
                </button>

                <div className="detail-mehr-huelle">
                  <button
                    type="button"
                    className="detail-mehr"
                    title="Weitere Aktionen"
                    aria-expanded={mehrOffen}
                    onClick={() => setMehrOffen((o) => !o)}
                  >
                    ⋯
                  </button>
                  {mehrOffen && (
                    <div className="detail-mehr-menue">
                      {a.ergaenzbareGewerke.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setMehrOffen(false);
                            setGewerkOffen(true);
                          }}
                        >
                          Gewerk ergänzen
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setMehrOffen(false);
                          setMailOffen(true);
                        }}
                      >
                        Erinnerungsmail ansehen
                      </button>
                      <button
                        type="button"
                        disabled={syncMutation.isPending}
                        onClick={() => {
                          setMehrOffen(false);
                          syncMutation.mutate();
                        }}
                      >
                        Abgleich erneut ausführen
                      </button>
                      {nutzer?.istAdmin && (
                        <button
                          type="button"
                          disabled={verknuepfungMutation.isPending}
                          title="Sucht Monday-Element und SharePoint-Ordner anhand der Nummer neu"
                          onClick={() => {
                            setMehrOffen(false);
                            verknuepfungMutation.mutate();
                          }}
                        >
                          {verknuepfungMutation.isPending
                            ? 'Sucht …'
                            : 'Verknüpfung neu suchen'}
                        </button>
                      )}
                      {nutzer?.istAdmin && (
                        <button
                          type="button"
                          className="detail-mehr-gefahr"
                          onClick={() => {
                            setMehrOffen(false);
                            setLoeschAbfrage(true);
                          }}
                        >
                          Angebot löschen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ---- Zeitachse: verortet den Status, wiederholt ihn nicht ---- */}
            <div className="detail-prozess">
              <div className="detail-abschnitt-titel">Verlauf im Prozess</div>
              <div className="detail-achse">
                {stationen.map((st, i) => {
                  const wann = erreicht.get(st);
                  const jetzt = st === a.status;
                  const zustand = jetzt ? 'jetzt' : wann ? 'erreicht' : 'offen';
                  return (
                    <div key={st} className={`detail-stufe ${zustand}`}>
                      <div className="detail-stufe-linie">
                        {i > 0 && <span className="detail-linie links" />}
                        <span className="detail-punkt" />
                        {i < stationen.length - 1 && <span className="detail-linie rechts" />}
                      </div>
                      <div className="detail-stufe-name">{st}</div>
                      <div className="detail-stufe-unter">
                        {jetzt ? (
                          <>
                            {a.alterText} im Status
                            {st === 'Gesendet' && a.versender ? ` · versendet von ${a.versender}` : ''}
                          </>
                        ) : wann ? (
                          [wann.datumText, wann.user].filter(Boolean).join(' · ')
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ---- Letzte Aktivität, Chronik dahinter ---- */}
            <div className="detail-aktivitaet">
              <div className="detail-aktivitaet-links">
                <span className="detail-aktivitaet-symbol">
                  {letzteAktivitaet?.art === 'rueckmeldung' ? (
                    <IconTelefon groesse={15} />
                  ) : letzteAktivitaet?.art === 'mail' ? (
                    <IconMail groesse={15} />
                  ) : (
                    <IconGesendet groesse={15} />
                  )}
                </span>
                <div className="detail-aktivitaet-text">
                  <div className="detail-abschnitt-titel">Letzte Aktivität</div>
                  <div className="detail-aktivitaet-zeile">
                    {letzteAktivitaet
                      ? `${letzteAktivitaet.titel} · ${letzteAktivitaet.zeitText}`
                      : 'Noch nichts geschehen.'}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="detail-chronik-knopf"
                onClick={() => setChronikOffen((o) => !o)}
              >
                Chronik {chronikOffen ? 'schließen' : 'öffnen'} ({chronik.length})
                <span className="detail-quelle-pfeil">{chronikOffen ? '▴' : '▾'}</span>
              </button>
            </div>

            {chronikOffen && (
              <div className="detail-chronik">
                <div className="detail-chronik-filter">
                  {(
                    [
                      ['alle', 'Alles'],
                      ['status', 'Status'],
                      ['aenderung', 'Änderungen'],
                      ['mail', 'Mails'],
                      ['rueckmeldung', 'Rückmeldungen'],
                    ] as const
                  ).map(([wert, label]) => (
                    <button
                      key={wert}
                      type="button"
                      className={`detail-filter-chip ${chronikFilter === wert ? 'aktiv' : ''}`}
                      onClick={() => setChronikFilter(wert)}
                    >
                      {label}
                      <span className="detail-filter-zahl">{chronikZahl[wert]}</span>
                    </button>
                  ))}
                </div>

                {/*
                  Eine Spur mit Symbolen statt einer Liste: Die Art des
                  Ereignisses ist auf einen Blick zu sehen, ohne sie in jeden
                  Titel zu schreiben. Die Linie hält die Einträge zusammen.
                */}
                <div className="detail-chronik-liste">
                  {chronikGefiltert.map((c, i) => (
                    <div key={c.id} className={`detail-chronik-eintrag art-${c.art}`}>
                      <div className="detail-chronik-spur">
                        <span className="detail-chronik-symbol">
                          {c.anlage ? (
                            '★'
                          ) : c.art === 'aenderung' ? (
                            <IconStift groesse={14} />
                          ) : c.art === 'rueckmeldung' ? (
                            <IconTelefon groesse={14} />
                          ) : c.art === 'mail' ? (
                            <IconMail groesse={14} />
                          ) : (
                            '→'
                          )}
                        </span>
                        {i < chronikGefiltert.length - 1 && <span className="detail-chronik-linie" />}
                      </div>

                      <div className="detail-chronik-inhalt">
                        <div className="detail-chronik-kopf">
                          <span className="detail-chronik-titel">{c.titel}</span>
                          <span className="detail-chronik-zeit">{c.zeitText}</span>
                        </div>
                        <div className="detail-chronik-unter">{c.unter}</div>
                        {c.notiz && <div className="detail-chronik-notiz">{c.notiz}</div>}
                      </div>
                    </div>
                  ))}
                  {chronikGefiltert.length === 0 && (
                    <div className="detail-leer">Nichts in dieser Auswahl.</div>
                  )}
                </div>
              </div>
            )}
            {loeschAbfrage && (
              <div className="detail-loeschfeld">
                <div className="detail-loeschtext">
                  Löscht {a.name} samt Verlauf, Rückmeldungen und Erinnerungen aus Kora. Das
                  Monday-Element und der SharePoint-Ordner bleiben bestehen und müssen dort von
                  Hand entfernt werden. Der Schritt lässt sich nicht rückgängig machen.
                </div>
                <div className="detail-loeschknoepfe">
                  <button
                    type="button"
                    className="detail-loeschen-ja"
                    disabled={loeschMutation.isPending}
                    onClick={() => loeschMutation.mutate()}
                  >
                    {loeschMutation.isPending ? 'Löscht …' : 'Endgültig löschen'}
                  </button>
                  <button type="button" onClick={() => setLoeschAbfrage(false)}>
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ---- Rechte Spalte: der Kunde und die Zahlen ---- */}
        <div className="detail-seitenspalte">
          <div className="detail-karte">
            <div className="detail-karte-kopf">
              <div className="detail-label">Kunde</div>
              <button
                type="button"
                className="detail-stiftknopf"
                title="Kundendaten bearbeiten"
                onClick={() => setKundeOffen(true)}
              >
                <IconStift groesse={14} />
              </button>
            </div>

            <div className="detail-kundenzeile">
              <span className="detail-kundenavatar">{a.kunde.initialen}</span>
              <div>
                <div className="detail-kundenname">{a.kunde.name}</div>
                <div className="detail-kundenmeta">
                  {a.kunde.typ === 'organisation' ? 'Organisation' : 'Privatkunde'} · {a.kunde.ort}
                </div>
              </div>
            </div>

            {/*
              Kontakt als schlichte Textlinks: Sie führen aus Kora hinaus und
              dürfen nicht mit dem Statuswechsel konkurrieren.

              Wohin der Klick führt, hängt von der Nummer ab. Eine Mobilnummer
              öffnet WhatsApp – so wird bei uns nachgefasst. Bei einem
              Festnetzanschluss wäre das eine Sackgasse, dort wählt der Klick.
              Die Mail geht über „mailto:" an das Standardprogramm, also an
              Outlook.
            */}
            <div className="detail-kontakt">
              {[
                { wert: a.kunde.mobil, was: 'Mobil' },
                { wert: a.kunde.mobilGeschaeftlich, was: 'Mobil geschäftlich' },
                { wert: a.kunde.telefon, was: 'Telefon' },
              ]
                .filter((n) => n.wert)
                .map((n) => {
                  const mobil = istMobil(n.wert);
                  return (
                    <a
                      key={n.was}
                      className="detail-kontaktlink"
                      href={mobil ? `https://wa.me/${whatsappNummer(n.wert)}` : `tel:${whatsappNummer(n.wert)}`}
                      target={mobil ? '_blank' : undefined}
                      rel={mobil ? 'noreferrer' : undefined}
                      title={mobil ? `${n.was} – öffnet WhatsApp` : `${n.was} – wählt die Nummer`}
                    >
                      <IconTelefon groesse={14} />
                      {n.wert}
                      {mobil && <span className="detail-kontakt-zusatz">WhatsApp</span>}
                    </a>
                  );
                })}

              {a.kunde.mail && (
                <a
                  className="detail-kontaktlink"
                  href={`mailto:${a.kunde.mail}`}
                  title="Öffnet Outlook mit einer neuen Nachricht"
                >
                  <IconMail groesse={14} />
                  {a.kunde.mail}
                </a>
              )}

              {!a.kunde.mail && !a.kunde.telefon && !a.kunde.mobil && !a.kunde.mobilGeschaeftlich && (
                <div className="detail-leer">Keine Kontaktdaten hinterlegt.</div>
              )}
            </div>

            {/*
              Personen werden benannt, nicht erklärt. Wer die Rolle innehat ist
              die Information – woher der Wert stammt, interessiert niemanden.
            */}
            <div className="detail-personen">
              <div className="detail-person">
                <span className="detail-personavatar">{a.kalkInitialen}</span>
                <div className="detail-persontext">
                  <div className="detail-personname">{a.kalkulant}</div>
                  <div className="detail-personrolle">Kalkulant</div>
                </div>
                {kalkWechsel ? (
                  <select
                    className="detail-kalk-auswahl"
                    value={a.kalkulant}
                    disabled={kalkulantMutation.isPending}
                    onChange={(e) => kalkulantMutation.mutate(e.target.value)}
                  >
                    {meta.team
                      .filter((t) => t.imVertrieb)
                      .map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                ) : (
                  <button
                    type="button"
                    className="detail-textknopf"
                    onClick={() => setKalkWechsel(true)}
                  >
                    wechseln
                  </button>
                )}
              </div>

              {versendet && a.versender && (
                <div className="detail-person">
                  <span className="detail-personavatar">{a.versInitialen}</span>
                  <div className="detail-persontext">
                    <div className="detail-personname">{a.versender}</div>
                    <div className="detail-personrolle">Versender</div>
                  </div>
                </div>
              )}
            </div>

            <div className="detail-datenzeile">
              <span className="detail-datenlabel">Nachfassen</span>
              <span className={`detail-datenwert ton-${nachfassTon}`} title={a.wvHerkunft}>
                {nachfassText}
              </span>
            </div>

            <div className="detail-datenzeile">
              <span className="detail-datenlabel">Angelegt</span>
              <span className="detail-datenwert">{dat(a.angelegtAm)}</span>
            </div>

            <div className="detail-datenzeile">
              <span className="detail-datenlabel">Auftragshöhe</span>
              <Betragsfeld
                wert={a.auftrag}
                laeuft={wertMutation.isPending}
                onSpeichern={(neu) => wertMutation.mutate(neu)}
              />
            </div>

            <div className="detail-hinweis">
              {a.status === 'Auftrag erteilt' || a.status === 'Abgenommen'
                ? 'Verbindlich – fließt in die Auswertung.'
                : 'Vorgemerkt. Fließt erst mit „Auftrag erteilt" in die Prognose.'}
            </div>
          </div>

          <div className="detail-karte">
            <div className="detail-label" style={{ marginBottom: 13 }}>
              Weitere Angebote des Kunden
            </div>
            <div className="detail-geschwister-liste">
              {geschwister.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="detail-geschwister-knopf"
                  onClick={() => navigate(`/angebot/${g.id}`, { state: { herkunft: herkunft.key } })}
                >
                  <span className="detail-geschwister-name">{g.name}</span>
                  <span className="detail-geschwister-meta">
                    {g.status} · {g.alterText}
                  </span>
                </button>
              ))}
              {geschwister.length === 0 && (
                <div className="detail-leer">Einzelangebot – keine weiteren Vorgänge.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {gewerkOffen && (
        <GewerkModal
          angebot={a}
          onSchliessen={() => setGewerkOffen(false)}
          onFertig={(neueId) => {
            aktualisieren();
            navigate(`/angebot/${neueId}`);
          }}
        />
      )}
      {versendetOffen && (
        <VersendetModal
          angebot={a}
          onSchliessen={() => setVersendetOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {auftragOffen && (
        <AuftragModal
          angebot={a}
          onSchliessen={() => setAuftragOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {anrufOffen && (
        <AnrufModal angebot={a} onSchliessen={() => setAnrufOffen(false)} onFertig={aktualisieren} />
      )}
      {mailOffen && <MailVorschauModal angebot={a} onSchliessen={() => setMailOffen(false)} />}
      {kundeOffen && (
        <KundenModal
          kundeId={a.kunde.id}
          onSchliessen={() => setKundeOffen(false)}
          onGespeichert={() => {
            setKundeOffen(false);
            aktualisieren();
          }}
        />
      )}
    </div>
  );
}